import { createAsyncThunk } from '@reduxjs/toolkit';
import * as changesets from 'json-diff-ts';
import { enqueueSnackbar } from 'notistack';

import { authApi } from './authApi';
import { fetchContractById } from './documentViewAPI';
import { normalizeDate } from '../DocumentLibrary/Component/TableComponents/helper';
import { SentencesData } from '../DocumentView/State/documentState';
import { setClauseLoading } from '../Redux/InsightSlice';

export const getExtractedData = createAsyncThunk(
  'insight/fetchExtractedData',
  async (draftId: string) => {
    const response: any = await authApi(
      `contracts/insights/${draftId}/extracted/`
    );
    const extracted: any[] = [];
    if (response.raw_content.hierarchy.length !== 0) {
      Object.keys(response.raw_content.hierarchy).map(function (key) {
        extracted.push(response.raw_content.hierarchy[key]);
      });
      extracted.sort((a: any, b: any) => (a.index > b.index ? 1 : -1));
    }
    const data = {
      fileId: draftId,
      hierarchy: extracted,
    };

    const parsed = data as SentencesData;
    return parsed;
  }
);

export const fetchInsightsSection = createAsyncThunk(
  'insight/fetchInsightsSection',
  async ({
    draftId,
    extractionType,
  }: {
    draftId: string;
    extractionType: string;
  }) => {
    if (extractionType === 'kdp') {
      return await authApi(`contracts/clause_types/${draftId}/`);
    } else {
      return await authApi(`contracts/ai_clause_types/${draftId}/`);
    }
  }
);

export const fetchClauseDataByType = createAsyncThunk(
  'insight/fetchClauseDataByType',
  async (params: {
    draftId?: string;
    clauseType: string;
    clauseId?: string;
  }) => {
    const response = params.clauseId
      ? await authApi(`contracts/ai_insights/${params.clauseId}/`)
      : await authApi(
          `contracts/insights/${params.draftId}/${params.clauseType}/`
        );

    const contractData: any = structuredClone(response);
    const edited = contractData.edited_content;

    let changeList;

    if (edited?.update?.length) {
      const diff = changesets.unflattenChanges(edited.update);
      changeList = changesets.applyChangeset(
        structuredClone(contractData.raw_content),
        diff
      );
    } else {
      changeList = structuredClone(contractData.raw_content);
    }

    return {
      [params.clauseType]: {
        ...contractData,
        changeList,
        loading: false,
      },
    };
  }
);

export const updateClauseById = createAsyncThunk(
  'insight/updateClauseById',
  async (params: any, thunkAPI) => {
    const { dispatch } = thunkAPI;
    const { fileId, clauseId, diff, updatedClauseData, type, oldData } = params;
    await dispatch(
      setClauseLoading({ [type]: { ...(oldData || {}), loading: true } })
    );

    const flatChanges = changesets.flattenChangeset(diff);
    let updated_payload: any = {
      edited_content: { update: flatChanges },
      copy_edited_content: updatedClauseData,
    };

    const startDate =
      updatedClauseData?.start_date?.data?.find(
        (date: any) => date?.normalized_date || date?.value
      )?.normalized_date ??
      updatedClauseData?.start_date?.data?.find(
        (date: any) => date?.normalized_date || date?.value
      )?.value ??
      null;

    const endDate =
      updatedClauseData?.end_date?.data?.find(
        (date: any) => date?.normalized_date || date?.value
      )?.normalized_date ??
      updatedClauseData?.end_date?.data?.find(
        (date: any) => date?.normalized_date || date?.value
      )?.value ??
      null;

    if (type === 'TERM CLAUSE') {
      updated_payload.start_date = startDate ? normalizeDate(startDate) : null;
      updated_payload.end_date = endDate ? normalizeDate(endDate) : null;
    }

    return await authApi
      .patch(`contracts/ai_insights/${clauseId}/`, updated_payload)
      .then(() => {
        enqueueSnackbar('Clause Updated successfully', { variant: 'success' });
        dispatch(
          fetchClauseDataByType({ clauseType: type, clauseId: clauseId })
        );
        dispatch(fetchContractById(atob(fileId)));
      });
    // return await authApi.post('contracts/update-es/', {
    //   index: type,
    //   contract_id: fileId,
    //   document: updatedClauseData,
    // });
  }
);

export const fetchCurrencyList = createAsyncThunk(
  'insight/fetchCurrencyList',
  async () => await authApi('admin/currency/')
);
